import React from 'react';
import PropTypes from 'prop-types';

const styles = {
  spinner: {
    display: 'inline-block',
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  spinnerInner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
};

// Add keyframes to document
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

const Spinner = ({
  size = 40,
  color = '#3498db',
  thickness = 4,
  backgroundColor = '#f3f3f3',
  fullScreen = false,
  overlay = false,
  overlayColor = 'rgba(0, 0, 0, 0.5)',
  className = '',
  style = {},
}) => {
  const spinnerStyle = {
    width: `${size}px`,
    height: `${size}px`,
    border: `${thickness}px solid ${backgroundColor}`,
    borderTop: `${thickness}px solid ${color}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    ...style,
  };

  const containerStyle = {
    ...styles.spinner,
    ...(fullScreen && styles.overlay),
    ...(overlay && {
      ...styles.overlay,
      backgroundColor: overlayColor,
    }),
  };

  return (
    <div className={`spinner-container ${className}`} style={containerStyle}>
      <div style={spinnerStyle} />
    </div>
  );
};

Spinner.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string,
  thickness: PropTypes.number,
  backgroundColor: PropTypes.string,
  fullScreen: PropTypes.bool,
  overlay: PropTypes.bool,
  overlayColor: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default Spinner;

// Loading Context để quản lý global loading state
export const LoadingContext = React.createContext({
  loading: false,
  setLoading: () => {},
});

export const LoadingProvider = ({ children }) => {
  const [loading, setLoading] = React.useState(false);

  return (
    <LoadingContext.Provider value={{ loading, setLoading }}>
      {children}
      {loading && <Spinner overlay />}
    </LoadingContext.Provider>
  );
};

// Custom hook để sử dụng loading
export const useLoading = () => {
  const context = React.useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

/**
 * 1. Basis usage
import Spinner from './utils/spinner';

// Simple spinner
<Spinner />

// Customized spinner
<Spinner 
  size={60}
  color="#ff0000"
  thickness={3}
  backgroundColor="#e0e0e0"
/>

// Full screen overlay spinner
<Spinner overlay />

* 2. Sử dụng với Loading Context:
// Trong file App.js hoặc root component
import { LoadingProvider } from './utils/spinner';

function App() {
  return (
    <LoadingProvider>
      <YourApp />
    </LoadingProvider>
  );
}

// Trong components con
import { useLoading } from './utils/spinner';

function YourComponent() {
  const { loading, setLoading } = useLoading();

  const handleAsyncAction = async () => {
    setLoading(true);
    try {
      await someAsyncOperation();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button onClick={handleAsyncAction}>Do Something</button>
    </div>
  );
}


* 3. Ví dụ sử dụng với API calls:
import { useLoading } from './utils/spinner';
import { get } from './utils/helpers';

function UserList() {
  const { setLoading } = useLoading();
  const [users, setUsers] = useState([]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await get('/users');
      if (!response.error) {
        setUsers(response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button onClick={fetchUsers}>Fetch Users</button>
      { Rest of your component }
      </div>
    );
  }


 */